import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CabinetShell from './CabinetShell';
import { statusMeta, fmtDate } from './orderConstants';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Plus, Loader2, AlertTriangle, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, accent }) => (
  <Card className="border-border" data-testid={`orders-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="py-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold ${accent || ''}`}>{value}</p>
    </CardContent>
  </Card>
);

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [o, s] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/orders/stats`),
      ]);
      setOrders(o.data);
      setStats(s.data);
    } catch (e) {
      toast.error('Erreur de chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <CabinetShell title="Mes commandes">
      <div className="flex justify-end mb-4">
        <Link to="/orders/new">
          <Button className="gap-2" data-testid="new-order-btn"><Plus className="h-4 w-4" />Nouvelle commande</Button>
        </Link>
      </div>
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="En cours" value={stats.in_progress} />
          <Stat label="Urgentes" value={stats.urgent} accent="text-orange-600" />
          <Stat label="Brouillons" value={stats.drafts} />
          <Stat label="Terminées" value={stats.completed} accent="text-green-600" />
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : orders.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-muted-foreground">
          <FolderOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>Aucune commande pour le moment.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {orders.map((o) => {
            const m = statusMeta(o.status);
            return (
              <Link key={o.id} to={`/orders/${o.id}`} className="block">
                <Card className="border-border hover:border-primary/40 transition-colors" data-testid={`order-row-${o.order_number}`}>
                  <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-sm">{o.order_number}</span>
                        {o.urgent && <AlertTriangle className="h-4 w-4 text-orange-500" />}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {o.patient_last_name ? `${o.patient_last_name} — ` : ''}{o.patient_ref} · {o.product_label}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</span>
                      <Badge variant="outline" className={m.cls}>{m.label}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </CabinetShell>
  );
}
